import React, { useState } from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  Box,
  Avatar,
  Menu,
  MenuItem,
  IconButton,
  Chip,
  useTheme,
  alpha,
  Tooltip
} from '@mui/material';
import {
  AccountCircle,
  Logout,
  Menu as MenuIcon,
  Settings,
  Person,
  AdminPanelSettings,
  OpenInNew
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate, useLocation } from 'react-router-dom';
import LoginDialog from './LoginDialog';
import Navigation from './Navigation';
import { useSidebarPrefs } from '../contexts/SidebarPrefsContext';

const Navbar = () => {
  const { user, logout, isAuthenticated } = useAuth();
  const [loginOpen, setLoginOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [navOpen, setNavOpen] = useState(false);
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();

  const isAdmin = user?.is_staff || user?.role === 'admin';
  const isShopFloor = location.pathname.startsWith('/shop-floor');
  const { pinnedActive } = useSidebarPrefs();
  // The landing dashboard docks the sidebar on desktop, so no hamburger there
  const hasDockedSidebar = location.pathname === '/dashboard';
  // Pinned sidebar is always visible, so the hamburger is redundant
  const hideHamburger = pinnedActive ? 'none' : hasDockedSidebar ? { xs: 'inline-flex', md: 'none' } : 'inline-flex';

  const handleProfileMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = async () => {
    console.log('NAVBAR LOGOUT CLICKED');
    console.log('Current pathname:', location.pathname);
    console.log('Is shop floor?', isShopFloor);
    
    await logout();
    console.log('Logout completed');
    
    handleMenuClose();
    console.log('Menu closed');
    
    if (isShopFloor) {
      console.log('Shop floor detected - reloading page');
      window.location.reload();
    } else {
      console.log('Not shop floor - navigating to home');
      navigate('/');
    }
  };

  const handleDuplicateTab = () => {
    window.open(window.location.href, '_blank');
  };

  const handleProfile = () => {
    navigate('/profile');
    handleMenuClose();
  };

  const handleSettings = () => {
    navigate('/settings');
    handleMenuClose();
  };

  return (
    <>
      <AppBar 
        position="sticky" 
        elevation={0}
        sx={{
          background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
          backdropFilter: 'blur(10px)',
          borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`
        }}
      >
        <Toolbar sx={{ justifyContent: 'space-between', py: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            {isAuthenticated && (
              <IconButton
                color="inherit"
                edge="start"
                onClick={() => setNavOpen(true)}
                sx={{ mr: 1, display: hideHamburger }}
              >
                <MenuIcon />
              </IconButton>
            )}
            <Tooltip title="Duplicate Tab">
              <IconButton
                color="inherit"
                onClick={handleDuplicateTab}
              >
                <OpenInNew />
              </IconButton>
            </Tooltip>
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: 2,
                background: 'linear-gradient(45deg, #FE6B8B 30%, #FF8E53 90%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontWeight: 'bold',
                color: 'white',
                fontSize: '1.2rem',
                cursor: 'pointer'
              }}
              onClick={() => navigate('/')}
            >
              G
            </Box>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 600,
                background: 'linear-gradient(45deg, #ffffff 30%, #f0f0f0 90%)',
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                cursor: 'pointer',
                display: { xs: 'none', sm: 'block' },
                whiteSpace: 'nowrap'
              }}
              onClick={() => navigate('/')}
            >
              Gopak Control System
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            {isAuthenticated ? (
              <>
                <Chip
                  icon={isAdmin ? <AdminPanelSettings /> : <AccountCircle />}
                  label={isAdmin ? 'Administrator' : user?.role || 'User'}
                  variant="outlined"
                  size="small"
                  sx={{
                    color: 'white',
                    borderColor: alpha(theme.palette.common.white, 0.3),
                    display: { xs: 'none', sm: 'flex' },
                    '&:hover': {
                      borderColor: alpha(theme.palette.common.white, 0.7),
                      backgroundColor: alpha(theme.palette.common.white, 0.1)
                    }
                  }}
                />
                <IconButton
                  size="large"
                  edge="end"
                  aria-label="account menu"
                  aria-controls="profile-menu"
                  aria-haspopup="true"
                  onClick={handleProfileMenuOpen}
                  sx={{ 
                    color: 'white',
                    border: `2px solid ${alpha(theme.palette.common.white, 0.3)}`,
                    '&:hover': {
                      backgroundColor: alpha(theme.palette.common.white, 0.1),
                      borderColor: alpha(theme.palette.common.white, 0.7)
                    }
                  }}
                >
                  <Avatar 
                    sx={{ 
                      width: 32, 
                      height: 32,
                      bgcolor: isAdmin ? theme.palette.error.main : theme.palette.secondary.main,
                      fontSize: '1rem',
                      fontWeight: 600
                    }}
                  >
                    {user?.username?.[0]?.toUpperCase() || 'U'}
                  </Avatar>
                </IconButton>
                <Menu
                  id="profile-menu"
                  anchorEl={anchorEl}
                  anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right',
                  }}
                  keepMounted
                  transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                  }}
                  open={Boolean(anchorEl)}
                  onClose={handleMenuClose}
                  PaperProps={{
                    sx: {
                      mt: 1,
                      minWidth: 250,
                      borderRadius: 2,
                      boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
                      border: `1px solid ${alpha(theme.palette.divider, 0.1)}`
                    }
                  }}
                >
                  <MenuItem onClick={handleProfile} sx={{ py: 1.5 }}>
                    <Person sx={{ mr: 2 }} />
                    <Box>
                      <Typography variant="body2" fontWeight={600}>
                        {user?.username}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {user?.email || 'View Profile'}
                      </Typography>
                    </Box>
                  </MenuItem>
                  <MenuItem onClick={handleSettings} sx={{ py: 1.5 }}>
                    <Settings sx={{ mr: 2 }} />
                    Settings
                  </MenuItem>
                  {isAdmin && (
                    <MenuItem 
                      onClick={() => window.open('/admin/', '_blank')} 
                      sx={{ py: 1.5 }}
                    >
                      <AdminPanelSettings sx={{ mr: 2 }} />
                      Django Admin
                    </MenuItem>
                  )}
                  <MenuItem onClick={handleLogout} sx={{ py: 1.5, color: 'error.main' }}>
                    <Logout sx={{ mr: 2 }} />
                    Sign Out
                  </MenuItem>
                </Menu>
              </>
            ) : (
              <Button
                variant="contained"
                onClick={() => setLoginOpen(true)}
                sx={{
                  bgcolor: 'white',
                  color: theme.palette.primary.main,
                  fontWeight: 600,
                  px: 3,
                  py: 1,
                  borderRadius: 2,
                  textTransform: 'none',
                  boxShadow: '0 4px 14px rgba(0,0,0,0.1)',
                  '&:hover': {
                    bgcolor: alpha(theme.palette.common.white, 0.9),
                    transform: 'translateY(-1px)',
                    boxShadow: '0 6px 20px rgba(0,0,0,0.15)'
                  },
                  transition: 'all 0.2s ease-in-out'
                }}
              >
                Sign In
              </Button>
            )}
          </Box>
        </Toolbar>
      </AppBar>

      {/* Pinned sidebar is rendered by AppRoutes in the page flow instead */}
      {!pinnedActive && (
        <Navigation open={navOpen} onClose={() => setNavOpen(false)} />
      )}
      <LoginDialog open={loginOpen} onClose={() => setLoginOpen(false)} />
    </>
  );
};

export default Navbar;